import React, { useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  Image,
  ScrollView,
  ActivityIndicator,
  Linking,
  TouchableOpacity,
} from 'react-native';
import { Color, CommonProperties } from '../styles';
import BaseText from '../components/BaseText';
import RNPickerSelect from 'react-native-picker-select';
import BottomButton from '../components/BottomButton';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Checkbox } from '../components/Checkbox';
import { activateUserApi, getActivationApi } from '../services/api/auth.service';
import { useDispatch } from 'react-redux';
import * as authActions from '../store/actions/auth';
import { scale } from '../utils/scale.utils';
import { getCountries } from '../utils/country.utils';
import { useNavigation } from '@react-navigation/native';
import { Modals } from '../constants/ScreenNames';
import { openAlert } from '../store/actions/alert';
import { contactAgentEMail, contactAgentTel, siteUrl } from '../config/env';
import DateInput from '../components/DateInput';
import OutlinedTextInput from '../components/OutlinedTextInput';
import Icon from '../components/Icon';

const ErrorText = ({ error }) => {
  if (!error) {
    return null;
  }
  return <BaseText style={styles.errorText}>{error}</BaseText>;
};

const UserActivationScreen = () => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const [isActivated, setIsActivated] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [userId, setUserId] = useState('');
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [form, setForm] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    preferredFirstName: '',
    preferredMiddleName: '',
    preferredLastName: '',
    receiptEmail: '',
    cellPhone: '',
    cellPhoneCode: 'US',
    newPassword: '',
    confirmPassword: '',
    securityQuestionID: '',
    securityQuestionID2: '',
    securityQuestionID3: '',
    securityAnswer: '',
    securityAnswer2: '',
    securityAnswer3: '',
    year: '',
    month: '',
    date: '',
    birthDate: '',
    id: '',
    gender: 'M',
    allowFullAccessCompanyAdmin: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [securityQuestions] = useState([
    // eslint-disable-next-line quotes
    { label: "What is your mother's maiden name?", value: '1' },
    { label: 'What was the name of your first pet?', value: '2' },
    { label: 'Where were you born?', value: '3' },
    { label: 'What is your favorite food?', value: '4' },
    { label: 'What is your favorite color?', value: '5' },
    { label: 'First foreign country you visited?', value: '6' },
    { label: 'Your first flight was to this city?', value: '7' },
    { label: 'Make and model of your first car?', value: '8' },
    { label: 'City where you had your first job?', value: '9' },
    { label: 'Your childhood nickname?', value: '10' },
    { label: 'Street where you lived when you were five?', value: '11' },
    { label: 'Name of your childhood best friend?', value: '12' },
    { label: 'First concert you attended?', value: '13' },
    { label: 'Favorite TV show when you were young?', value: '14' },
    { label: 'Strangest food you have ever eaten?', value: '15' },
    { label: 'Last name of favourite elementary school teacher?', value: '16' },
    { label: 'City where you met your spouse/partner?', value: '17' },
  ]);

  useEffect(() => {
    const validateAndFetchData = async () => {
      const url = new URL(window.location.href);
      const path = url.pathname;
      const params = new URLSearchParams(url.search);
      const value = params.get('value');

      if (path === '/Admin/user/activate' && value) {
        try {
          setIsFetching(true);
          const response = await getActivationApi(url);
          setIsFetching(false);
          if (response.Message) {
            setIsActivated(true);
            setTimeout(() => {
              navigation.navigate(Modals.Login);
            }, 3000);
          } else {
            setUserId(response.Id);
            setUser({
              firstName: response.FirstName || '',
              lastName: response.LastName || '',
              email: response.Email || '',
            });

            setForm((prevForm) => ({
              ...prevForm,
              firstName: response.FirstName || '',
              lastName: response.LastName || '',
              receiptEmail: response.Email || '',
            }));
          }
        } catch (error) {
          dispatch(
            openAlert({
              title: 'Invalid Activation Link',
              message: 'Please request a new link or contact support for assistance.',
              confirmText: 'Okay',
              onConfirmPressed: () => navigation.navigate(Modals.Login),
            })
          );
          console.error('Error fetching activation data:', error);
        }
      } else {
        dispatch(
          openAlert({
            title: 'Invalid Activation Link',
            message:
              'The activation link is invalid. Please request a new link or contact support.',
            confirmText: 'Okay',
            onConfirmPressed: () => navigation.navigate(Modals.Login),
          })
        );
        console.log('Invalid URL');
      }
    };

    validateAndFetchData();
  }, [dispatch, navigation]);

  useEffect(() => {
    (async () => {
      const res = await getCountries();
      setCountryList(res);
    })();
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!form.firstName) {
      newErrors.firstName = 'Legal First Name is required';
    }
    if (!form.lastName) {
      newErrors.lastName = 'Legal Last Name is required';
    }
    if (!form.receiptEmail) {
      newErrors.receiptEmail = 'Receipt Email is required';
    }
    if (!form.receiptEmail.match(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/)) {
      newErrors.receiptEmail = 'Invalid email address';
    }
    if (!form.cellPhone) {
      newErrors.cellPhone = 'Cell Phone is required';
    }
    if (!form.newPassword) {
      newErrors.newPassword = 'New Password is required';
    }
    if (
      !form.newPassword.match(/^(?=.*[A-Z])(?=.*[0-9])(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,16}$/)
    ) {
      newErrors.newPassword =
        'Password must contain one capital letter, one number, one special character, and be 6-16 characters long';
    }
    if (!form.confirmPassword) {
      newErrors.confirmPassword = 'Repeat New Password is required';
    }
    if (form.newPassword !== form.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!form.securityAnswer) {
      newErrors.securityAnswer = 'Answer 1 is required';
    }
    if (!form.securityAnswer2) {
      newErrors.securityAnswer2 = 'Answer 2 is required';
    }
    if (!form.securityAnswer3) {
      newErrors.securityAnswer3 = 'Answer 3 is required';
    }
    if (!form.year || !form.month || !form.date) {
      newErrors.date = 'Date of Birth is required';
    }
    if (!form.cellPhoneCode) {
      newErrors.cellPhoneCode = 'Country is required';
    }
    if (!form.gender || form.gender.trim() === '') {
      newErrors.gender = 'Gender is required';
    }

    if (!form.securityQuestionID) {
      newErrors.securityQuestionID = 'Security Question 1 is required';
    }
    if (!form.securityQuestionID2) {
      newErrors.securityQuestionID2 = 'Security Question 2 is required';
    }
    if (!form.securityQuestionID3) {
      newErrors.securityQuestionID3 = 'Security Question 3 is required';
    }

    const selectedQuestions = [
      form.securityQuestionID,
      form.securityQuestionID2,
      form.securityQuestionID3,
    ].filter(Boolean);

    if (
      selectedQuestions.length > 0 &&
      new Set(selectedQuestions).size !== selectedQuestions.length
    ) {
      newErrors.securityQuestionID = 'Please select different security questions';
      newErrors.securityQuestionID2 = 'Please select different security questions';
      newErrors.securityQuestionID3 = 'Please select different security questions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (name, value) => {
    setForm({ ...form, [name]: value });
  };

  const handleBirthDate = (date) => {
    if (date) {
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const birthDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      setForm({
        ...form,
        year: year,
        month: month,
        date: day,
        birthDate: birthDate,
      });
    }
  };

  const handleSubmit = async () => {
    if (validate()) {
      try {
        setIsSubmitting(true);
        const data = {
          User: {
            ...form,
            id: userId,
            UserProfile: {
              CellPhone: form.cellPhone,
              CellPhoneCode: form.cellPhoneCode,
            },
          },
          BirthYear: form.year,
          BirthMonth: form.month,
          Birth_Date: form.date,
        };
        const response = await activateUserApi(data);
        if (response.Message !== '') {
          const userName = response.Email;
          const password = response.NewPassword;
          await dispatch(authActions.userAuthenticate({ userName, password }));
        }
      } catch (error) {
        console.error('Activation error:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      console.log('Form has errors', errors);
    }
  };

  const passwordCriteria = [
    'One capital letter',
    'One number',
    'One special character (@#%& etc.)',
    'Length 6-16 characters',
  ];

  const genderList = [
    { label: 'Male', value: 'M' },
    { label: 'Female', value: 'F' },
    { label: 'Unspecified', value: 'O' },
  ];

  const openLink = (path) => {
    Linking.openURL(`https://${siteUrl}${path}`).catch((err) =>
      console.error('Error opening URL:', err)
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.logoContainer}>
        <Image style={styles.logo} source={require('../assets/img/logo.png')} />
        <View style={styles.divider} />
      </View>

      {isFetching ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Color.primary} />
          <BaseText style={styles.loadingText}>Loading...</BaseText>
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.scrollContainer}>
          {isActivated ? (
            <View style={styles.activatedContainer}>
              <View style={styles.activatedContent}>
                <BaseText style={styles.activatedTitle}>User Already Activated</BaseText>
                <BaseText style={styles.activatedSubtitle}>
                  Please wait while we redirect you to the login screen...
                </BaseText>
              </View>
            </View>
          ) : (
            <>
              <BaseText
                style={styles.title}
              >{`Hello ${user.firstName} ${user.lastName},`}</BaseText>
              <BaseText style={styles.emailText}>Login Name: {user.email}</BaseText>
              <BaseText style={styles.introText}>
                Welcome To TripEasy! Please complete your required profile and travel security
                administration detail to initiate your account setup.
              </BaseText>
              <View style={styles.inputContainer}>
                <OutlinedTextInput
                  label={'Legal First Name*'}
                  value={form.firstName}
                  onChangeText={(text) => handleChange('firstName', text)}
                  error={errors.firstName}
                  style={styles.input}
                />

                <OutlinedTextInput
                  label={'Legal Middle Name'}
                  value={form.middleName}
                  onChangeText={(text) => handleChange('middleName', text)}
                  style={styles.input}
                />

                <OutlinedTextInput
                  label={'Legal Last Name*'}
                  value={form.lastName}
                  onChangeText={(text) => handleChange('lastName', text)}
                  error={errors.lastName}
                  style={styles.input}
                />

                <OutlinedTextInput
                  label={'Preferred First Name'}
                  value={form.preferredFirstName}
                  onChangeText={(text) => handleChange('preferredFirstName', text)}
                  style={styles.input}
                />

                <OutlinedTextInput
                  label={'Preferred Middle Name'}
                  value={form.preferredMiddleName}
                  onChangeText={(text) => handleChange('preferredMiddleName', text)}
                  style={styles.input}
                />

                <OutlinedTextInput
                  label={'Preferred Last Name'}
                  value={form.preferredLastName}
                  onChangeText={(text) => handleChange('preferredLastName', text)}
                  style={styles.input}
                />

                <OutlinedTextInput
                  label={'Receipt Email*'}
                  value={form.receiptEmail}
                  onChangeText={(text) => handleChange('receiptEmail', text)}
                  error={errors.receiptEmail}
                  keyboardType="email-address"
                  style={styles.input}
                />

                <View style={styles.row}>
                  <View style={styles.countryCode}>
                    <BaseText style={styles.labelText}>Country*</BaseText>
                    <RNPickerSelect
                      onValueChange={(value) => handleChange('cellPhoneCode', value)}
                      items={countryList}
                      value={form.cellPhoneCode}
                      placeholder={{ label: 'Select country', value: null }}
                      style={pickerSelectStyles}
                      Icon={() => <Icon name="chevronDown" size={15} color={Color.gray} />}
                    />
                    <ErrorText error={errors.cellPhoneCode} />
                  </View>

                  <View style={styles.phoneContainer}>
                    <OutlinedTextInput
                      label={'Cell Phone*'}
                      keyboardType="phone-pad"
                      value={form.cellPhone}
                      onChangeText={(text) => handleChange('cellPhone', text)}
                      error={errors.cellPhone}
                      style={styles.input}
                    />
                  </View>
                </View>

                <View>
                  <BaseText style={styles.sectionHeader}>Enter New Password</BaseText>
                  <View style={styles.dividerSection} />

                  <OutlinedTextInput
                    label={'New Password*'}
                    value={form.newPassword}
                    secureTextEntry={true}
                    onChangeText={(text) => handleChange('newPassword', text)}
                    error={errors.newPassword}
                    style={styles.input}
                  />

                  <OutlinedTextInput
                    label={'Repeat New Password*'}
                    value={form.confirmPassword}
                    secureTextEntry={true}
                    onChangeText={(text) => handleChange('confirmPassword', text)}
                    error={errors.confirmPassword}
                    style={styles.input}
                  />

                  <BaseText style={styles.infoText}>Your password must be:</BaseText>
                  {passwordCriteria.map((criterion, index) => (
                    <View key={index} style={styles.criteriaRow}>
                      <View style={styles.bullet} />
                      <BaseText style={styles.criteriaText}>{criterion}</BaseText>
                    </View>
                  ))}
                </View>

                <View>
                  <BaseText style={styles.sectionHeader}>TSA Information</BaseText>
                  <View style={styles.dividerSection} />
                  <BaseText style={styles.infoText}>
                    Information required for the Transportation Security Administration.
                  </BaseText>

                  <DateInput
                    date={new Date()}
                    onDateChange={(date) => handleBirthDate(date)}
                    label={'Date of Birth*'}
                  />
                  <ErrorText error={errors.date} />

                  <BaseText style={styles.labelText}>Gender*</BaseText>
                  <RNPickerSelect
                    onValueChange={(value) => handleChange('gender', value)}
                    items={genderList}
                    value={form.gender}
                    placeholder={{ label: 'Select gender', value: null }}
                    style={pickerSelectStyles}
                    Icon={() => <Icon name="chevronDown" size={15} color={Color.gray} />}
                  />
                  <ErrorText error={errors.gender} />

                  <View style={styles.row}>
                    <Checkbox
                      style={styles.checkbox}
                      selected={form.allowFullAccessCompanyAdmin}
                      onPress={() =>
                        handleChange(
                          'allowFullAccessCompanyAdmin',
                          !form.allowFullAccessCompanyAdmin
                        )
                      }
                    />
                    <BaseText style={styles.allowAccessText}>
                      Allow access to all of my account profile information for my travel program
                      manager or company administrator for booking assistance.
                    </BaseText>
                  </View>
                </View>

                <View>
                  <BaseText style={styles.sectionHeader}>Select Security Questions</BaseText>
                  <View style={styles.dividerSection} />
                  <BaseText style={styles.infoText}>
                    Please select and answer the security questions below. This will be used to
                    verify your identity should the need arise.
                  </BaseText>

                  <BaseText style={styles.labelText}>Security Question 1</BaseText>
                  <RNPickerSelect
                    onValueChange={(value) => handleChange('securityQuestionID', value)}
                    items={securityQuestions}
                    value={form.securityQuestionID}
                    placeholder={{ label: 'Select security question', value: null }}
                    style={pickerSelectStyles}
                    Icon={() => <Icon name="chevronDown" size={15} color={Color.gray} />}
                  />
                  <ErrorText error={errors.securityQuestionID} />
                  <OutlinedTextInput
                    label={'Answer 1*'}
                    value={form.securityAnswer}
                    onChangeText={(text) => handleChange('securityAnswer', text)}
                    error={errors.securityAnswer}
                    style={styles.securityAnswer}
                  />

                  <BaseText style={styles.labelText}>Security Question 2</BaseText>
                  <RNPickerSelect
                    onValueChange={(value) => handleChange('securityQuestionID2', value)}
                    items={securityQuestions}
                    value={form.securityQuestionID2}
                    placeholder={{ label: 'Select security question', value: null }}
                    style={pickerSelectStyles}
                    Icon={() => <Icon name="chevronDown" size={15} color={Color.gray} />}
                  />
                  <ErrorText error={errors.securityQuestionID2} />
                  <OutlinedTextInput
                    label={'Answer 2*'}
                    value={form.securityAnswer2}
                    onChangeText={(text) => handleChange('securityAnswer2', text)}
                    error={errors.securityAnswer2}
                    style={styles.securityAnswer}
                  />

                  <BaseText style={styles.labelText}>Security Question 3</BaseText>
                  <RNPickerSelect
                    onValueChange={(value) => handleChange('securityQuestionID3', value)}
                    items={securityQuestions}
                    value={form.securityQuestionID3}
                    placeholder={{ label: 'Select security question', value: null }}
                    style={pickerSelectStyles}
                    Icon={() => <Icon name="chevronDown" size={15} color={Color.gray} />}
                  />
                  <ErrorText error={errors.securityQuestionID3} />
                  <OutlinedTextInput
                    label={'Answer 3*'}
                    value={form.securityAnswer3}
                    onChangeText={(text) => handleChange('securityAnswer3', text)}
                    error={errors.securityAnswer3}
                    style={styles.securityAnswer}
                  />
                </View>

                <View style={styles.row}>
                  <Checkbox
                    style={styles.checkbox}
                    selected={privacyPolicyAccepted}
                    onPress={() => setPrivacyPolicyAccepted(!privacyPolicyAccepted)}
                  />
                  <View style={styles.privacyTextContainer}>
                    <BaseText style={styles.privacyText}>
                      I agree to the{' '}
                      <TouchableOpacity onPress={() => openLink('/web/privacypolicy')}>
                        <BaseText style={styles.linkText}>privacy policy</BaseText>
                      </TouchableOpacity>{' '}
                      and{' '}
                      <TouchableOpacity onPress={() => openLink('/web/termsconditions')}>
                        <BaseText style={styles.linkText}>Terms of Use</BaseText>
                      </TouchableOpacity>
                      .
                    </BaseText>
                  </View>
                </View>
              </View>
              <BottomButton
                label={isSubmitting ? 'Activating...' : 'Activate'}
                onPress={handleSubmit}
                disabled={!privacyPolicyAccepted || isSubmitting}
                loading={isSubmitting}
              />

              <View style={styles.footer}>
                <View style={styles.divider} />
                <Image style={styles.footerLogo} source={require('../assets/img/logo.png')} />
                <View style={styles.footerRow}>
                  <TouchableOpacity onPress={() => openLink('/Home/About')}>
                    <BaseText>About</BaseText>
                  </TouchableOpacity>
                  <BaseText>|</BaseText>
                  <TouchableOpacity onPress={() => openLink('/Home/Rewards')}>
                    <BaseText>Rewards</BaseText>
                  </TouchableOpacity>
                  <BaseText>|</BaseText>
                  <TouchableOpacity onPress={() => openLink('/Home/Contact')}>
                    <BaseText>Contact</BaseText>
                  </TouchableOpacity>
                  <BaseText>|</BaseText>
                  <TouchableOpacity onPress={() => openLink('/Home/Team')}>
                    <BaseText>Team</BaseText>
                  </TouchableOpacity>
                </View>
                <View style={styles.footerRow2}>
                  <TouchableOpacity onPress={() => openLink('/web/termsconditions')}>
                    <BaseText>Terms</BaseText>
                  </TouchableOpacity>
                  <BaseText>|</BaseText>
                  <TouchableOpacity onPress={() => openLink('/web/privacypolicy')}>
                    <BaseText>Privacy</BaseText>
                  </TouchableOpacity>
                  <BaseText>|</BaseText>
                </View>
                <View>
                  <BaseText style={styles.footerText}>
                    Reservation support: {contactAgentTel} or {contactAgentEMail}
                  </BaseText>
                  <BaseText style={styles.footerText}>
                    Copyright © {new Date().getFullYear()} Internet Travel Solutions LLC.
                  </BaseText>
                </View>
              </View>
            </>
          )}
        </ScrollView>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  logoContainer: {
    alignItems: 'center',
    ...CommonProperties.mt5,
  },
  logo: {
    resizeMode: 'cover',
    width: '90%',
    height: scale(60),
    maxWidth: scale(300),
    ...CommonProperties.mb3,
  },
  divider: {
    width: '100%',
    height: scale(2),
    backgroundColor: Color.primary,
    ...CommonProperties.m3,
    ...CommonProperties.shadow,
  },
  dividerSection: {
    width: '100%',
    height: scale(1),
    backgroundColor: Color.gray,
    marginVertical: scale(10),
    alignSelf: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    ...CommonProperties.mx2,
    ...CommonProperties.my3,
    columnGap: 10,
  },
  countryCode: {
    flex: 1,
  },
  checkbox: {
    justifyContent: 'center',
  },
  title: {
    ...CommonProperties.h1,
    ...CommonProperties.my3,
    textAlign: 'center',
  },
  emailText: {
    textAlign: 'center',
    ...CommonProperties.h4,
  },
  introText: {
    textAlign: 'center',
    ...CommonProperties.mt3,
    ...CommonProperties.h4,
  },
  footerText: {
    textAlign: 'center',
    ...CommonProperties.h5,
    ...CommonProperties.mt2,
  },
  infoText: {
    textAlign: 'center',
    ...CommonProperties.mt3,
    ...CommonProperties.mb5,
    ...CommonProperties.h4,
    ...CommonProperties.textBold,
  },
  allowAccessText: {
    textAlign: 'center',
    ...CommonProperties.h4,
    ...CommonProperties.textBold,
  },
  criteriaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    ...CommonProperties.ml5,
  },
  bullet: {
    width: scale(6),
    height: scale(6),
    ...CommonProperties.borderRadius3,
    backgroundColor: Color.gray,
    ...CommonProperties.mr3,
  },
  criteriaText: {
    color: Color.textPrimary,
    ...CommonProperties.body,
  },
  inputContainer: {
    width: '100%',
    ...CommonProperties.mt5,
  },
  input: {
    ...CommonProperties.my3,
  },
  securityAnswer: {
    ...CommonProperties.my2,
  },
  scrollContainer: {
    flexGrow: 1,
    ...CommonProperties.mx4,
    ...CommonProperties.mb3,
  },
  sectionHeader: {
    ...CommonProperties.h2,
    ...CommonProperties.mt5,
    textAlign: 'center',
  },
  privacyText: {
    flex: 1,
    ...CommonProperties.h4,
    ...CommonProperties.textBold,
  },
  privacyTextContainer: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  linkText: {
    ...CommonProperties.h4,
    color: Color.primary,
  },
  footer: {
    ...CommonProperties.mt5,
    alignItems: 'center',
  },
  footerLogo: {
    resizeMode: 'cover',
    width: '90%',
    height: scale(50),
    maxWidth: scale(200),
    ...CommonProperties.my3,
  },
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '80%',
    ...CommonProperties.px5,
    ...CommonProperties.my2,
  },
  footerRow2: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    width: '60%',
    ...CommonProperties.px5,
    ...CommonProperties.my2,
  },
  errorText: {
    color: Color.required,
    ...CommonProperties.body,
    ...CommonProperties.ml1,
    marginTop: -5,
    ...CommonProperties.mb2,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    ...CommonProperties.h3,
    ...CommonProperties.mt3,
  },
  activatedContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  activatedContent: {
    alignItems: 'center',
    paddingHorizontal: scale(20),
  },
  activatedTitle: {
    ...CommonProperties.h1,
    color: Color.primary,
    textAlign: 'center',
    ...CommonProperties.mb3,
  },
  activatedSubtitle: {
    ...CommonProperties.h3,
    color: Color.textPrimary,
    textAlign: 'center',
  },
  phoneContainer: {
    flex: 2,
  },
  labelText: {
    color: Color.textPrimary,
    ...CommonProperties.body,
    marginBottom: scale(5),
  },
});

const pickerSelectStyles = StyleSheet.create({
  inputIOS: {
    fontSize: scale(14),
    paddingVertical: scale(12),
    paddingHorizontal: scale(10),
    borderWidth: 1,
    borderRadius: 5,
    color: Color.textPrimary,
    paddingRight: scale(30),
    marginBottom: scale(15),
  },
  inputAndroid: {
    fontSize: scale(14),
    paddingHorizontal: scale(10),
    paddingVertical: scale(8),
    borderWidth: 1,
    borderRadius: 5,
    color: Color.textPrimary,
    paddingRight: scale(30),
    marginBottom: scale(15),
  },
  inputWeb: {
    fontSize: scale(14),
    paddingVertical: scale(5),
    paddingHorizontal: scale(10),
    borderWidth: 1,
    borderRadius: 5,
    color: Color.textPrimary,
    paddingRight: scale(30),
    marginBottom: scale(20),
    backgroundColor: Color.accent,
    height: scale(50),
    width: '100%',
    appearance: 'none',
  },
  iconContainer: {
    top: scale(15),
    right: scale(10),
  },
});

export default UserActivationScreen;
